import { useCallback, useEffect, useRef, useState } from "react";
import { getRulebookCard } from "../rulebookAttachmentClient";
var CardLookup;
(function (CardLookup) {
    CardLookup["Loading"] = "loading";
    /** `GET /rulebooks/{id}` answered 404: the Rulebook is gone or not visible to the user. */
    CardLookup["Missing"] = "missing";
    /** A 404 being re-checked after a list reload: still displayed as missing until the result lands. */
    CardLookup["Rechecking"] = "rechecking";
    /** The request failed otherwise; retried when the user's list reloads (e.g. the picker opens). */
    CardLookup["Failed"] = "failed";
})(CardLookup || (CardLookup = {}));
/** Sets (or, with `undefined`, clears) the lookup state of one Rulebook id. */
const withLookup = (rulebookId, status) => (previous) => {
    const next = { ...previous };
    if (status) {
        next[rulebookId] = status;
    }
    else {
        delete next[rulebookId];
    }
    return next;
};
/**
 * A reloaded list re-tries transient failures and re-checks 404s: `Failed` clears, `Missing` becomes `Rechecking`
 * (a fresh lookup may fire, while the section keeps showing missing until the answer lands).
 */
const withoutSettledFailures = (previous) => {
    const entries = Object.entries(previous);
    if (!entries.some(([, status]) => status === CardLookup.Failed || status === CardLookup.Missing)) {
        return previous;
    }
    const next = {};
    for (const [rulebookId, status] of entries) {
        if (status === CardLookup.Failed) {
            continue;
        }
        next[rulebookId] = status === CardLookup.Missing ? CardLookup.Rechecking : status;
    }
    return next;
};
const withoutKnown = (rulebookId) => (previous) => {
    if (!(rulebookId in previous)) {
        return previous;
    }
    const next = { ...previous };
    delete next[rulebookId];
    return next;
};
/** Every remembered card is eligible for one background revalidation after each list reload. */
const markAllStale = (previous) => {
    const entries = Object.entries(previous);
    return entries.length ? Object.fromEntries(entries.map(([rulebookId, known]) => [rulebookId, { ...known, isStale: true }])) : previous;
};
const withFresh = (rulebookId) => (previous) => {
    const known = previous[rulebookId];
    return known ? { ...previous, [rulebookId]: { ...known, isStale: false } } : previous;
};
/**
 * A revalidated card overwrites only the fields it actually carries: the server view may lack `cover_url`,
 * `description`, `tags` or `creator_username`, so a fetch must not blank what a pick already showed.
 */
const mergeCard = (previous, fetched) => {
    const merged = { ...previous };
    for (const [field, value] of Object.entries(fetched)) {
        if (value !== undefined) {
            merged[field] = value;
        }
    }
    return merged;
};
/**
 * `GET /rulebooks/{id}` fires only for the picked id the list can't explain: no card yet, or a card that went stale
 * on the last list reload, and no lookup already concluded or in flight (`Rechecking` counts as "no lookup yet").
 */
const resolveLookupId = ({ isEnabled, currentId, isListSettled, fromList, known, lookup, }) => isEnabled && currentId && isListSettled && !fromList && (!known || known.isStale) && (lookup === undefined || lookup === CardLookup.Rechecking)
    ? currentId
    : null;
/**
 * The card for the picked or attached Rulebook. It comes from the user's own list, else from a summary the picker
 * handed over (a Discover pick), else from `GET /rulebooks/{id}` — an attached public Rulebook isn't in the user's
 * list. Only a 404 from that lookup reports the Rulebook missing; other failures are retried when the list reloads.
 * Cards are stale-while-revalidated on every list reload: the old card keeps showing, a successful refresh merges
 * defined fields over it (a Discover pick's presentation extras survive), a failed one is silent, and a 404 drops
 * it to missing.
 */
export default function useCurrentRulebookCard({ currentId, rulebooks, hasListError, isEnabled, }) {
    const [stateKnown, setStateKnown] = useState({});
    const [stateLookup, setStateLookup] = useState({});
    // One token per in-flight lookup: a card remembered meanwhile (a pick) cancels the stale response.
    const refTokens = useRef(new Map());
    const remember = useCallback((rulebook) => {
        refTokens.current.delete(rulebook.id);
        setStateKnown((previous) => ({ ...previous, [rulebook.id]: { card: rulebook, isStale: false } }));
        // A card in hand settles any earlier lookup for that id, including a 404.
        setStateLookup(withLookup(rulebook.id, undefined));
    }, []);
    // A reloaded list is a natural retry point for lookups that failed or went missing, and ages every
    // remembered card into a background revalidation.
    useEffect(() => {
        setStateLookup(withoutSettledFailures);
        setStateKnown(markAllStale);
    }, [rulebooks]);
    const fromList = currentId ? rulebooks?.find((rulebook) => rulebook.id === currentId) : undefined;
    const known = currentId ? stateKnown[currentId] : undefined;
    const lookup = currentId ? stateLookup[currentId] : undefined;
    // Most picks are the user's own, so wait for their list first — unless it failed, which mustn't hide the card.
    const isListSettled = rulebooks !== null || hasListError;
    const lookupId = resolveLookupId({ isEnabled, currentId, isListSettled, fromList, known, lookup });
    useEffect(() => {
        if (!lookupId || refTokens.current.has(lookupId)) {
            return;
        }
        const wasRevalidating = stateKnown[lookupId] !== undefined;
        const token = Symbol(lookupId);
        refTokens.current.set(lookupId, token);
        // A re-check after a 404 keeps Rechecking (still displayed missing) instead of flipping to a spinner.
        setStateLookup((previous) => (previous[lookupId] === CardLookup.Rechecking ? previous : withLookup(lookupId, CardLookup.Loading)(previous)));
        const settle = () => {
            if (refTokens.current.get(lookupId) !== token) {
                return false;
            }
            refTokens.current.delete(lookupId);
            return true;
        };
        getRulebookCard(lookupId)
            .then((card) => {
            if (!settle()) {
                return;
            }
            if (card) {
                setStateKnown((previous) => {
                    const existing = previous[lookupId]?.card;
                    // A cold lookup is stored whole but fills `tags` — the fetched card leaves it absent when the view has
                    // none, so the merge can't overwrite a pick's tags; a cold card still needs a defined field.
                    const fetched = existing ? mergeCard(existing, card) : { ...card, tags: card.tags ?? [] };
                    return { ...previous, [lookupId]: { card: fetched, isStale: false } };
                });
                setStateLookup(withLookup(lookupId, undefined));
            }
            else {
                setStateLookup(withLookup(lookupId, CardLookup.Missing));
                setStateKnown(withoutKnown(lookupId));
            }
        })
            .catch(() => {
            if (!settle()) {
                return;
            }
            if (wasRevalidating) {
                // A failed revalidation keeps the old card and just drops the stale flag — the lookup must clear
                // with it, or Loading would block every future revalidation of this id.
                setStateKnown(withFresh(lookupId));
                setStateLookup(withLookup(lookupId, undefined));
            }
            else {
                setStateLookup(withLookup(lookupId, CardLookup.Failed));
            }
        });
    }, [lookupId, stateKnown]);
    const retryCardLookup = useCallback(() => {
        setStateLookup((previous) => (currentId && previous[currentId] === CardLookup.Failed ? withLookup(currentId, undefined)(previous) : previous));
    }, [currentId]);
    const rulebook = fromList ?? known?.card ?? null;
    return {
        rulebook,
        isResolving: lookup === CardLookup.Loading && !rulebook,
        isMissing: !rulebook && (lookup === CardLookup.Missing || lookup === CardLookup.Rechecking),
        /** The card lookup failed outright — the attachment can't be read, same surface as the attachment lookup. */
        hasCardError: lookup === CardLookup.Failed,
        retryCardLookup,
        remember,
    };
}
